/**
 * Human-readable default org name from a business website URL (not "Jane's Organization").
 */

/** Common business words embedded in concatenated domain slugs (e.g. arapahoefamilydentistry). */
const DOMAIN_SLUG_KEYWORDS = [
  "orthodontics",
  "orthodontic",
  "dentistry",
  "dental",
  "physicaltherapy",
  "physical",
  "therapy",
  "chiropractic",
  "chiropractor",
  "veterinary",
  "pharmacy",
  "wellness",
  "family",
  "health",
  "healthcare",
  "medical",
  "clinic",
  "center",
  "centre",
  "group",
  "associates",
  "partners",
  "services",
  "service",
  "solutions",
  "consulting",
  "insurance",
  "law",
  "legal",
  "attorney",
  "attorneys",
  "plumbing",
  "hvac",
  "roofing",
  "construction",
  "properties",
  "realty",
  "real",
  "estate",
  "garage",
  "doors",
  "auto",
  "automotive",
  "fitness",
  "studio",
  "salon",
  "spa",
  "restaurant",
  "cafe",
  "coffee",
  "hotel",
  "inn",
  "school",
  "academy",
  "church",
  "community",
  "foundation",
  "company",
  "corp",
  "inc",
  "llc",
] as const;

function titleCaseWord(word: string): string {
  const trimmed = word.trim();
  if (!trimmed) return "";
  if (trimmed.length <= 3 && trimmed === trimmed.toUpperCase()) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/** Split a lowercase domain slug into spaced words using known business keywords. */
export function splitConcatenatedDomainSlug(slug: string): string | null {
  const normalized = slug.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (!normalized || normalized.length < 6) return null;
  if (normalized.includes(" ")) return null;

  const keywords = [...DOMAIN_SLUG_KEYWORDS].sort((a, b) => b.length - a.length);
  const matches: Array<{ index: number; keyword: string }> = [];

  for (const keyword of keywords) {
    let start = 0;
    while (start < normalized.length) {
      const index = normalized.indexOf(keyword, start);
      if (index === -1) break;
      matches.push({ index, keyword });
      start = index + keyword.length;
    }
  }

  if (matches.length === 0) return null;

  matches.sort((a, b) => a.index - b.index);

  const parts: string[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.index < cursor) continue;
    if (match.index > cursor) {
      parts.push(normalized.slice(cursor, match.index));
    }
    parts.push(match.keyword);
    cursor = match.index + match.keyword.length;
  }
  if (cursor < normalized.length) {
    parts.push(normalized.slice(cursor));
  }

  const words = parts.filter(Boolean).map(titleCaseWord).filter(Boolean);
  if (words.length < 2) return null;

  return words.join(" ").slice(0, 120);
}

export function suggestedOrganizationNameFromWebsiteUrl(url: string): string {
  try {
    const normalized = url.startsWith("http") ? url : `https://${url}`;
    const host = new URL(normalized).hostname.replace(/^www\./, "");
    const seg = host.split(".")[0] ?? "Practice";
    const spaced = seg.replace(/[-_]+/g, " ").trim();
    if (!spaced) return "My practice";

    if (!spaced.includes(" ")) {
      const fromSlug = splitConcatenatedDomainSlug(spaced);
      if (fromSlug) return fromSlug;
    }

    const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase());
    return titled.slice(0, 120);
  } catch {
    return "My practice";
  }
}
